import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Icon } from "./Icon.tsx";
import { ApiError } from "../lib/api.ts";

export function Stat({ label, value, color, children }: { label: string; value: ReactNode; color?: string; children?: ReactNode }) {
  return (
    <div className="card stat">
      <span className="stat-label"><span className="tick" style={color ? { background: color } : undefined} />{label}</span>
      <span className="stat-value">{value}</span>
      {children}
    </div>
  );
}

/** Modal dialog with Escape to close and focus moved inside. */
export function Dialog({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEscape(onClose);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>("input, select, textarea, button:not([data-close])")?.focus();
  }, []);
  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <div ref={ref} role="dialog" aria-modal="true" aria-label={title} className={`dialog${wide ? " dialog-wide" : ""}`}>
        <div className="overlay-head">
          <h2>{title}</h2>
          <CloseButton onClick={onClose} label="Fechar" />
        </div>
        {children}
      </div>
    </>
  );
}

export function Drawer({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEscape(onClose);
  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <aside role="dialog" aria-modal="true" aria-label={title} className="drawer">
        <div className="overlay-head">
          <h2>{title}</h2>
          <CloseButton onClick={onClose} label="Fechar detalhe" />
        </div>
        {children}
      </aside>
    </>
  );
}

export function CloseButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" data-close className="btn btn-icon" style={{ background: "var(--chip)", height: 32, width: 32 }} onClick={onClick} aria-label={label}>
      <Icon name="x" size={16} stroke={2.2} />
    </button>
  );
}

function useEscape(fn: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && fn();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [fn]);
}

const MESSAGES: Record<string, string> = {
  forbidden: "Sua função não permite esta ação.",
  city_forbidden: "Você não tem acesso a esta cidade.",
  reason_required: "Informe o motivo.",
  documents_required: "Escolha os documentos que precisam ser reenviados.",
  documents_incomplete: "Faltam documentos obrigatórios para aprovar.",
  not_awaiting_review: "Este cadastro já foi decidido por outra pessoa.",
  email_domain: "Use um e-mail do Google Workspace da empresa.",
  invalid_cities: "Escolha ao menos uma cidade válida.",
  cannot_grant: "Você não pode conceder acesso maior que o seu.",
  cannot_edit_self: "Você não pode alterar o próprio acesso.",
  already_member: "Essa pessoa já faz parte da equipe.",
  invalid_code: "Código incorreto. Confira o app autenticador.",
  too_many_attempts: "Muitas tentativas. Aguarde 15 minutos.",
  not_cancellable: "Esta corrida já começou ou terminou e não pode ser cancelada.",
  not_refundable: "Esta corrida não tem pagamento para reembolsar.",
  not_active: "Este motorista não está ativo.",
  not_suspended: "Este motorista não está suspenso.",
  invalid_value: "Valor inválido para esta configuração.",
  no_recipients: "Ninguém nesse público para notificar.",
  cannot_edit_own_role: "Você não pode editar a sua própria função.",
  invalid_permissions: "Permissões inválidas para uma função.",
  role_not_found: "Esta função não existe mais. Recarregue a página.",
  role_in_use: "Há membros com esta função. Mude a função deles antes de excluir.",
  invalid_id: "Escolha um nome com letras ou números.",
  city_not_found: "Esta cidade não existe ou você não tem acesso a ela.",
};
/** Known codes first, then the API's own pt-BR `message`, then the bare code. */
export const errorMessage = (e: unknown) => {
  if (!(e instanceof ApiError)) return "Não foi possível concluir. Tente de novo.";
  const body = e.body as { message?: unknown } | null;
  return MESSAGES[e.code] ?? (typeof body?.message === "string" && body.message ? body.message : `Erro ${e.status}: ${e.code}`);
};

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  return <div className="error-box" role="alert">{errorMessage(error)}</div>;
}

/** Short-lived confirmation message. */
export function useToast() {
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(null), 3200);
    return () => clearTimeout(t);
  }, [msg]);
  return { show: setMsg, node: msg ? <div className="toast" role="status">{msg}</div> : null };
}

/** Route error boundary (router defaultErrorComponent): the message, a retry, and the details for a bug report. */
export function RouteError({ error, reset }: { error: unknown; reset: () => void }) {
  const err = error instanceof Error ? error : new Error(String(error));
  return (
    <section className="card crash" role="alert">
      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="pill tone-bad">Erro</span>
        <b style={{ fontSize: 16 }}>Esta tela encontrou um problema</b>
      </span>
      <span className="muted">{error instanceof ApiError ? errorMessage(error) : "Algo deu errado ao mostrar esta tela. Tente de novo; se continuar, envie os detalhes abaixo para o time."}</span>
      <div className="action-bar">
        <button type="button" className="btn btn-primary" onClick={reset}><Icon name="refresh" size={16} />Tentar de novo</button>
        <button type="button" className="btn" onClick={() => window.location.reload()}>Recarregar a página</button>
      </div>
      <details style={{ width: "100%" }}>
        <summary className="muted" style={{ cursor: "pointer", fontSize: 13 }}>Detalhes técnicos</summary>
        <pre>{err.message}{err.stack ? `\n\n${err.stack.split("\n").slice(1, 8).join("\n")}` : ""}</pre>
      </details>
    </section>
  );
}

/** On/off switch (settings, ride types). */
export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return <button type="button" role="switch" className="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)} />;
}

/**
 * Confirmation for a consequential action, with an optional required reason (audited server side).
 * `onConfirm` gets the reason; errors stay in the dialog.
 */
export function ConfirmDialog({
  title, body, confirmLabel, danger, reason, reasonLabel = "Motivo", reasonPlaceholder, confirmDisabled, children, onConfirm, onClose,
}: {
  title: string; body?: ReactNode; confirmLabel: string; danger?: boolean; reason?: boolean; reasonLabel?: string; reasonPlaceholder?: string; confirmDisabled?: boolean;
  children?: ReactNode; onConfirm: (reason: string) => Promise<unknown>; onClose: () => void;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const ok = (!reason || text.trim().length >= 3) && !confirmDisabled;
  async function go() {
    setBusy(true);
    setErr(null);
    try {
      await onConfirm(text.trim());
      onClose();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog title={title} onClose={onClose}>
      {body && <div className="muted" style={{ margin: 0, lineHeight: 1.5 }}>{body}</div>}
      {children}
      {reason && (
        <label className="field">{reasonLabel}
          <textarea className="textarea" value={text} onChange={(e) => setText(e.target.value)} placeholder={reasonPlaceholder} maxLength={300} />
        </label>
      )}
      <ErrorBox error={err} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="btn" onClick={onClose}>Cancelar</button>
        <button type="button" className={`btn ${danger ? "btn-danger" : "btn-primary"}`} disabled={!ok || busy} onClick={go}>{confirmLabel}</button>
      </div>
    </Dialog>
  );
}

export function Skeleton({ h = 18, w = "100%" }: { h?: number; w?: number | string }) {
  return <div className="skeleton" style={{ height: h, width: w }} />;
}

/**
 * A person's avatar: their photo (a short-lived link from the API) over the initials, which show
 * while it loads, without a photo, or when it fails (expired link, dev placeholder).
 */
export function Initials({ text, photo, light, size, className, style }: {
  text: string; photo?: string | null; light?: boolean; size?: number; className?: string; style?: CSSProperties;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  return (
    <span
      className={`avatar${light ? " avatar-light" : ""}${className ? ` ${className}` : ""}`}
      style={size || style ? { ...(size ? { width: size, height: size } : {}), ...style } : undefined}
      aria-hidden="true"
    >
      {text}
      {photo && photo !== failed && <img className="avatar-photo" src={photo} alt="" referrerPolicy="no-referrer" onError={() => setFailed(photo)} />}
    </span>
  );
}
